import { NavLink } from 'react-router-dom';
import {
  LayoutGrid,
  Store,
  Receipt,
  ClipboardList,
  LogOut,
  Printer,
  CreditCard,
  TicketCheck,
  ArrowLeftRight,
  Gift,
  Undo2,
} from 'lucide-react';
import { useAuthStore } from '@/stores/auth.store';
import { useShiftAwareLogout } from '@/hooks/useShiftAwareLogout';
import { useDeviceStore } from '@/stores/device.store';
import { useShallow } from 'zustand/react/shallow';
import { cn } from '@/lib/utils';
import { PERM, posRedirectFor, useCan, usePermissionSet } from '@/lib/permissions';
import { useT, type MessageKey } from '@/lib/i18n';
import { LanguageSwitch } from '@/components/pos/LanguageSwitch';

const NAV: Array<{ to: string; label: MessageKey; icon: typeof Store; end: boolean }> = [
  { to: '/menu', label: 'nav.home', icon: LayoutGrid, end: false },
  { to: '/', label: 'nav.sell', icon: Store, end: true },
  { to: '/foc', label: 'nav.foc', icon: Gift, end: false },
  { to: '/orders', label: 'nav.orders', icon: ClipboardList, end: false },
  { to: '/print-ticket', label: 'nav.print', icon: TicketCheck, end: false },
  { to: '/manage-addons', label: 'nav.addon', icon: ArrowLeftRight, end: false },
  { to: '/shift', label: 'nav.shift', icon: Receipt, end: false },
];

/** Shown only to operators who may request a void / refund. */
const REQUESTS_NAV = { to: '/requests', label: 'nav.requests' as MessageKey, icon: Undo2, end: false };

export function CommandRail() {
  const { t } = useT();
  const user = useAuthStore((s) => s.user);
  const canVoid = useCan(PERM.VOID_REQUEST);
  const canRefund = useCan(PERM.REFUND_REQUEST);
  const permissionSet = usePermissionSet();
  // Only the pages the route gate would let this account stay on.
  const nav = (canVoid || canRefund ? [...NAV, REQUESTS_NAV] : NAV).filter(
    (item) => posRedirectFor(permissionSet, item.to) === null,
  );
  const { printerBound, readerBound } = useDeviceStore(
    useShallow((s) => ({
      printerBound: Boolean(s.ticketPrinterId),
      readerBound: Boolean(s.cashlessReaderId),
    })),
  );

  // With a shift open, "Keluar" asks: close the shift, or just sign out.
  const { requestLogout: handleLogout, dialog: logoutDialog } = useShiftAwareLogout();

  return (
    <nav className="rail-surface flex h-full w-24 shrink-0 flex-col items-center justify-between py-4 text-cream-50 shadow-rail">
      <div className="flex flex-col items-center gap-6">
        {/* High-res primary mark rendered crisp white (the dedicated white asset is low-res). */}
        <img
          src="/logo-autopia-primary.png"
          alt="Autopia"
          className="w-[68px] max-w-full object-contain"
          style={{ filter: 'brightness(0) invert(1)' }}
        />
        <div className="h-px w-12 rail-hairline border-t" />
        <ul className="flex flex-col gap-2">
          {nav.map(({ to, label, icon: Icon, end }) => (
            <li key={to}>
              <NavLink
                to={to}
                end={end}
                className={({ isActive }) =>
                  cn(
                    'group relative flex w-16 flex-col items-center gap-1 rounded-xl px-1 py-2.5 text-cream-100 transition-all',
                    'hover:bg-white/10 hover:text-cream-50',
                    isActive && 'bg-white/15 text-cream-50',
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive && (
                      <span className="absolute -left-4 top-1/2 h-8 w-1.5 -translate-y-1/2 rounded-full bg-gold-500" />
                    )}
                    <Icon className="h-6 w-6" />
                    <span className="font-sans text-[11px] font-semibold">{t(label)}</span>
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </div>

      <div className="flex flex-col items-center gap-3">
        <div className="flex flex-col items-center gap-1.5">
          <span title={t(printerBound ? 'rail.printerBound' : 'rail.printerUnbound')}>
            <Printer className={cn('h-4 w-4', printerBound ? 'text-gold-500' : 'text-white/40')} />
          </span>
          <span title={t(readerBound ? 'rail.readerBound' : 'rail.readerUnbound')}>
            <CreditCard className={cn('h-4 w-4', readerBound ? 'text-gold-500' : 'text-white/40')} />
          </span>
        </div>
        <LanguageSwitch tone="rail" />
        <div className="h-px w-12 rail-hairline border-t" />
        <div className="flex flex-col items-center text-center">
          <span className="line-clamp-1 max-w-[64px] text-[10px] font-semibold text-cream-50">
            {user?.name ?? user?.email ?? t('rail.cashier')}
          </span>
          <span className="text-[9px] font-medium uppercase tracking-wide text-gold-200">
            {user?.role}
          </span>
        </div>
        <button
          type="button"
          onClick={handleLogout}
          className="flex w-16 flex-col items-center gap-1 rounded-xl py-2 text-cream-100 transition-colors hover:bg-white/10 hover:text-gold-500"
        >
          <LogOut className="h-5 w-5" />
          <span className="text-[10px] font-semibold">{t('rail.logout')}</span>
        </button>
      </div>
      {logoutDialog}
    </nav>
  );
}
